// Helpers для portal-session create/resume (используется M5-плеером).
//
// Identity-keyed cookies (BUG-4099): per-course session cookie ключуется на
// (portalId, courseId, identity). Если в том же браузере залогинились другим
// email — кука с прошлой identity недостижима, "Continue or start new" не
// предлагается ошибочно.
import { createPortalSession } from 'components/contentspace/api'
import {
  getPortalAuthCookie,
  getSessionCookie,
  identityKeyFromAuth,
  setPortalAuthCookie,
  setSessionCookie,
  type IIdentityKey,
} from 'components/contentspace/sessionCookie'
import type { IContentSpaceSession } from 'components/contentspace/types'

export interface IPortalSessionResult {
  session: IContentSpaceSession
  sessionToken: string
  /** True when the session was freshly created (not resumed from cookie). */
  isNew: boolean
  identity: IIdentityKey
}

const ensureAnonIdentity = (portalId: string): IIdentityKey => {
  const auth = getPortalAuthCookie(portalId) ?? { verified: false }
  const existing = identityKeyFromAuth(auth)
  if (existing) {
    return existing
  }
  // Bootstrap анонимной identity. anonSessionId живёт 30d вместе с кукой,
  // переживает retry/reopen в том же браузере, не делится с другим юзером.
  const anonSessionId = crypto.randomUUID()
  setPortalAuthCookie(portalId, { ...auth, anonSessionId })
  return { kind: 'anon', short: anonSessionId }
}

export const getOrCreatePortalSession = async (
  companyId: string,
  portalId: string,
  courseId: string,
  opts: { forceNew?: boolean } = {},
): Promise<IPortalSessionResult> => {
  const portalAuth = getPortalAuthCookie(portalId)
  const identity = identityKeyFromAuth(portalAuth) ?? ensureAnonIdentity(portalId)

  const existingCookie = opts.forceNew ? null : getSessionCookie(portalId, courseId, identity)

  if (existingCookie?.sessionId && existingCookie?.sessionToken) {
    // M5 fetch-нет полную сессию через getSessionById для cmiData — здесь
    // отдаём shell, чтобы M5 решил сам.
    const shell: IContentSpaceSession = {
      id: existingCookie.sessionId,
      courseId,
      type: identity.kind === 'anon' ? 'ANONYMOUS' : 'EMAIL',
      status: 'IN_PROGRESS',
      sessionToken: existingCookie.sessionToken,
      email: portalAuth?.email ?? null,
      emailVerified: portalAuth?.verified ?? false,
      userName: portalAuth?.name ?? null,
      kuserId: portalAuth?.kuserId ?? null,
      progress: 0,
      score: null,
      scoreMin: null,
      scoreMax: null,
      location: null,
      cmiData: null,
      isDebug: false,
      startedAt: null,
      completedAt: null,
      lastAccessAt: null,
      createdAt: new Date().toISOString(),
    }
    return { session: shell, sessionToken: existingCookie.sessionToken, isNew: false, identity }
  }

  const type = portalAuth?.email ? 'EMAIL' : 'ANONYMOUS'

  const { session, sessionToken } = await createPortalSession(companyId, portalId, courseId, {
    type,
    email: portalAuth?.email,
    userName: portalAuth?.name,
    forceNew: opts.forceNew,
  })

  const sessionWithToken: IContentSpaceSession = { ...session, sessionToken }
  setSessionCookie(portalId, courseId, identity, { sessionId: session.id, sessionToken })

  return { session: sessionWithToken, sessionToken, isNew: true, identity }
}
